import React, { useState, useEffect } from "react";

const FALLBACK_BASE_PRICES = [
  {
    id: "1",
    title: "Meget lille hund (under 5 kg)",
    description: "F.eks. Chihuahua, Yorkshire Terrier, Toy Puddel, Malteser hvalp",
    service_1_name: "Vask, føn & negleklip",
    service_1_price: "",
    service_2_name: "Komplet klip, vask & negleklip",
    service_2_price: "",
    note: "Inkl. blid håndtering og plejende shampoo",
    order: 1,
  },
  {
    id: "2",
    title: "Lille hund (5 – 10 kg)",
    description: "F.eks. Shih Tzu, Malteser, Bichon Frisé, Gravhund, Mops, Dværgschnauzer",
    service_1_name: "Vask, føn & negleklip",
    service_1_price: "",
    service_2_name: "Komplet klip, vask & negleklip",
    service_2_price: "",
    note: "Vores mest populære kategori",
    order: 2,
  },
  {
    id: "3",
    title: "Mellem hund (10 – 20 kg)",
    description: "F.eks. Cocker Spaniel, Mellempuddel, Dansk-Svensk Gårdhund, Corgi",
    service_1_name: "Vask, føn & negleklip",
    service_1_price: "",
    service_2_name: "Komplet klip, vask & negleklip",
    service_2_price: "",
    note: "Efter forudgående aftale",
    order: 3,
  },
  {
    id: "4",
    title: "Større hund (over 20 kg)",
    description: "F.eks. Golden Retriever, Labrador, Labradoodle, mfl.",
    service_1_name: "Vask, føn & negleklip",
    service_1_price: "",
    service_2_name: "Komplet klip, vask & negleklip",
    service_2_price: "",
    note: "Forespørgsel og individuel vurdering",
    order: 4,
  },
];

const FALLBACK_ADDON_PRICES = [
  {
    id: "1",
    title: "Udbørstning af filter / filtret pels",
    description: "Skånsom og tålmodig udredning af filtret pels og knuder for hundens velbefindende",
    price: "",
    note: "Pris pr. påbegyndt 30 min",
    order: 1,
  },
  {
    id: "2",
    title: "Loppebad & desinficering",
    description: "Specialbad med loppeshampoo samt efterfølgende grundig rengøring og desinficering af salonen",
    price: "",
    note: "Obligatorisk tillæg ved konstaterede lopper",
    order: 2,
  },
  {
    id: "3",
    title: "Kun negleklip",
    description: "Skånsom klipning og filing af klør (uden vask eller klip)",
    price: "",
    note: "Hurtig tidsbestilling mulig",
    order: 3,
  },
  {
    id: "4",
    title: "Øreplukning & ørerens",
    description: "Fjernelse af hår i øregangen samt rensning med milde, beroligende plejeprodukter",
    price: "",
    note: "Forebygger fugt og irritation",
    order: 4,
  },
  {
    id: "5",
    title: "Medicinsk / specialshampoo",
    description: "Ekstra beroligende, kløestillende eller allergivenlig shampoo til sensitiv hud",
    price: "",
    note: "Tilkøb efter hundens individuelle behov",
    order: 5,
  },
  {
    id: "6",
    title: "Poterens & poteklip",
    description: "Klip af pels mellem trædepuderne samt pleje med nærende potesalve",
    price: "",
    note: "Ideelt mod vejsalt om vinteren og tørre poter",
    order: 6,
  },
];

export default function DogPricingTable() {
  const [basePrices, setBasePrices] = useState(FALLBACK_BASE_PRICES);
  const [addonPrices, setAddonPrices] = useState(FALLBACK_ADDON_PRICES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch("/_api/public/v2/content/hundeklip_grundpriser?sort=order&order=asc&limit=50")
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
      fetch("/_api/public/v2/content/hundeklip_tillaegspriser?sort=order&order=asc&limit=50")
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ])
      .then(([baseData, addonData]) => {
        if (baseData?.items && Array.isArray(baseData.items) && baseData.items.length > 0) {
          setBasePrices(baseData.items);
        }
        if (addonData?.items && Array.isArray(addonData.items) && addonData.items.length > 0) {
          setAddonPrices(addonData.items);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const formatPrice = (val) => {
    if (!val || String(val).trim() === "") {
      return "– kr.";
    }
    const str = String(val).trim();
    if (!str.toLowerCase().includes("kr") && !str.toLowerCase().includes("kontakt") && !isNaN(str.replace(/\./g, "").replace(/,/g, ""))) {
      return `${str} kr.`;
    }
    return str;
  };

  return (
    <section id="priser" className="py-14 lg:py-20 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="mb-12">
          <div className="max-w-2xl text-left space-y-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-[#2C2523]">
              Priser for hundeklip, bad & tillægspleje
            </h2>
            <p className="text-base text-[#5C524C] leading-relaxed">
              Oversigt over grundpriser baseret på hundens størrelse samt mulige tillæg for ekstra pleje og særlige behov.
            </p>
          </div>
        </div>

        {/* 2-KOLONNE OVERSIGT: GRUNDPRISER (VENSTRE) OG TILLÆGSPRISER (HØJRE) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* VENSTRE KOLONNE: GRUNDPRISER (EFTER STØRRELSE) */}
          <div className="space-y-6">
              <div className="border-b border-[#EADFCF] pb-3">
                <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                  Grundpriser efter størrelse
                </h3>
                <p className="text-xs text-[#887055] mt-1">
                  Vask, føn, klip og negleklip opdelt efter hundens vægt og race
                </p>
              </div>

              {/* Simpel opstilling uden kasser */}
              <div className="divide-y divide-[#EADFCF]/70">
                {basePrices.map((pkg) => (
                  <div
                    key={pkg.id || pkg.title}
                    className="py-4 first:pt-0 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1 pr-2">
                        <h4 className="text-base font-semibold text-[#2C2523] leading-snug">
                          {pkg.title}
                        </h4>
                        {pkg.description && (
                          <p className="text-xs text-[#6B5D55] leading-relaxed">
                            {pkg.description}
                          </p>
                        )}
                        {pkg.note && (
                          <p className="text-[11px] text-[#887055] italic">
                            {pkg.note}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Prislinjer for ydelsen */}
                    <div className="space-y-1.5 pt-1 pl-1">
                      {pkg.service_1_name && (
                        <div className="flex items-center justify-between text-xs text-[#3A302B]">
                          <span className="text-[#5C524C]">{pkg.service_1_name}</span>
                          <span className="font-bold text-sm text-[#2C2523]">
                            {formatPrice(pkg.service_1_price)}
                          </span>
                        </div>
                      )}
                      {pkg.service_2_name && (
                        <div className="flex items-center justify-between text-xs text-[#3A302B]">
                          <span className="text-[#5C524C]">{pkg.service_2_name}</span>
                          <span className="font-bold text-sm text-[#2C2523]">
                            {formatPrice(pkg.service_2_price)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          {/* HØJRE KOLONNE: TILLÆGSPRISER & PLEJE */}
          <div className="space-y-6">
              <div className="border-b border-[#EADFCF] pb-3">
                <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                  Tillægspriser for hunde klip og pleje
                </h3>
                <p className="text-xs text-[#887055] mt-1">
                  Ekstra ydelser og tillæg ved særlige behov eller specifikke ønsker
                </p>
              </div>

              {/* Simpel, overskuelig opstilling uden kasser */}
              <div className="divide-y divide-[#EADFCF]/70">
                {addonPrices.map((addon) => (
                  <div
                    key={addon.id || addon.title}
                    className="py-4 first:pt-0 flex items-start justify-between gap-4"
                  >
                    <div className="space-y-1 pr-2">
                      <h4 className="text-sm font-semibold text-[#2C2523] leading-snug">
                        {addon.title}
                      </h4>
                      {addon.description && (
                        <p className="text-xs text-[#6B5D55] leading-relaxed">
                          {addon.description}
                        </p>
                      )}
                      {addon.note && (
                        <p className="text-[11px] text-[#887055] italic">
                          {addon.note}
                        </p>
                      )}
                    </div>
                    <span className="text-sm font-bold text-[#2C2523] whitespace-nowrap pt-0.5">
                      {formatPrice(addon.price || addon.single_price || addon.service_1_price)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
        </div>
      </div>
    </section>
  );
}
